import { i18n } from "@tellescope/i18n";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";

export const DEFAULT_CONTACT_DETAILS_VIEW_ID = "__default-view-id__" as const;

/**
 * Whether a `custom_dashboards` view id refers to a record that actually exists on the server.
 *
 * The placeholder view is synthesized client-side by {@link getDefaultView}, so requesting it would
 * always come back empty. Use this to gate any `custom_dashboards` query keyed on a view id.
 */
export const isFetchableViewId = (viewId: string | undefined): boolean =>
  !!viewId && viewId !== DEFAULT_CONTACT_DETAILS_VIEW_ID;

/**
 * The dedicated sentinel view id for the standalone Contact Timeline takeover page
 * (`/contacts/timeline/:enduserId`). This must never be promoted to a real saved view on write —
 * unlike {@link DEFAULT_CONTACT_DETAILS_VIEW_ID}, which is promotable via useWritableViewId. See the
 * hard guard in use-writable-view-id.ts.
 */
export const CONTACT_TIMELINE_TAKEOVER_VIEW_ID = "__contact-timeline-takeover-view__" as const;

/**
 * Whether a `custom_dashboards` view id both exists on the server AND is safe to treat as a normal,
 * writable view. Unlike {@link isFetchableViewId} (which only excludes the promotable placeholder),
 * this also excludes {@link CONTACT_TIMELINE_TAKEOVER_VIEW_ID}, which may never be fetched, resolved,
 * or written to.
 */
export const isRealViewId = (viewId: string | undefined): boolean =>
  isFetchableViewId(viewId) && viewId !== CONTACT_TIMELINE_TAKEOVER_VIEW_ID;

/**
 * Identifies the synthetic Timeline block rendered on the standalone Contact Timeline takeover page.
 * Fixed (not per-instance) because the page always renders exactly one synthesized block.
 *
 * `TimelineBlock` uses this to suppress every control that would write back to
 * {@link CONTACT_TIMELINE_TAKEOVER_VIEW_ID} — the filter/combine-SMS popover and the duplicate/delete
 * actions — since that view is never fetched (see {@link isRealViewId}), which makes `useBlock`'s
 * `usePermissionQuery` fail open (`record: undefined` resolves to `access: true`). Without this, those
 * controls would render live-looking but silently no-op (`useBlockMutations` resolves against an empty,
 * uncached view and returns early).
 */
export const CONTACT_TIMELINE_TAKEOVER_BLOCK_ID = "__contact-timeline-takeover-block__" as const;
const DEFAULT_VIEW_TYPE_PREFIX = "__T2__";

/**
 * The `custom_dashboards.type` that marks a record as a contact-details view.
 *
 * Reads of `custom_dashboards` must filter on this: the model is shared with other dashboard
 * features, and this one appends blocks with `replaceObjectFields: true`, so an unfiltered read can
 * select a record it does not own and overwrite its contents. Exported so the filter and the value
 * {@link getDefaultView} stamps come from one place — a filter that drifted from what gets written
 * would silently match nothing.
 */
export const CONTACT_DETAILS_VIEW_TYPE = `${DEFAULT_VIEW_TYPE_PREFIX}-contacts-dialog` as const;

export const getDefaultView = (options: { typePrefix?: string } = { typePrefix: DEFAULT_VIEW_TYPE_PREFIX }) =>
  ({
    id: DEFAULT_CONTACT_DETAILS_VIEW_ID,
    title: i18n.t("Patient Information", { ns: "contacts" }),
    type: `${options.typePrefix ?? ""}-contacts-dialog`,
    blocks: [],
    businessId: "",
    createdAt: new Date(0),
    updatedAt: new Date(0),
    creator: "",
  }) as const satisfies TellescopeModelFromName<"custom_dashboards">;
